/**
 * BACKOFFICE LOGISTIQUE — Models
 */

import { FlightCity, FlightSchedule } from '../utils/flight-schedule';
import type { VmOrderType } from '../../vm/models/vm-account.model';

// Stats dashboard
export interface BackofficeStats {
  totalShipments: number;
  byType: { standard: number; heavy: number; vm: number };
  /**
   * Phase RÉSOLUE (racine `currentPhase`, sinon `phase.current`, sinon dérivée
   * de `status.current`). Vue « au mieux » : elle compte des colis que les
   * listes filtrées ne peuvent PAS afficher.
   */
  byPhase: Record<string, number>;
  /**
   * #987 — phase telle que la LISTE la filtre : champ racine `currentPhase`
   * UNIQUEMENT. C'est ce que les onglets de `/backoffice/shipments` doivent
   * afficher, sinon un badge annonce des colis que l'onglet ne montrera jamais
   * (cas UAT A7-001, infaisable avant ce correctif).
   */
  byRootPhase: Record<string, number>;
  /**
   * #987 — colis sans `currentPhase` racine : comptés dans `byPhase`, jamais
   * listables. Mesuré sur `befret-acceptance` : 3 sur 62. Exposé pour être
   * AFFICHÉ, pas pour être noyé dans un total.
   */
  withoutRootPhase: number;
  /**
   * #987 / #416 — l'échantillon des N colis les plus récents a atteint son
   * plafond : les `byPhase`/`byRootPhase` sont alors des MINORANTS, et leur
   * somme ne peut pas égaler `totalShipments` (compté, lui, côté serveur).
   */
  sampleCapped: boolean;
  sampleSize: number;
  receptionToday: number;
  pendingWeighing: number;
  pendingPreparation: number;
  openContainers: number;
  pendingDeliveries: number;
}

// Item dans la liste "Colis (all)"
export interface ShipmentListItem {
  id: string;
  trackingNumber: string;
  dpdTrackingNumber: string;
  type: string;
  category: string;
  currentPhase: string;
  statusCurrent: string;
  senderName: string;
  senderEmail: string;
  senderCountry: string;
  receiverName: string;
  receiverCity: string;
  weight: number;
  befretWeight?: number;
  deliveryMethod: string;
  depositMethod: string;
  emballage: boolean;
  /** #2122 — `null` si aucune date n'est lisible sur le document. */
  createdAt: Date | null;
  paymentStatus?: string;
  weightDiscrepancyStatus?: string;
  weightDiscrepancyType?: string;
  weightDiscrepancyAmount?: number;
  destinationZone?: string;
  transportMode?: string;
  /** Fin de préparation (`preparation.sortedAt`), `null` si absente. */
  sortedAt?: Date | null;
  preparationCondition?: string;
  assignedPartnerId?: string;
  assignedPartnerName?: string;
  // #1121 — champs VM exposés sur les lignes de LISTE (recherche par boîte /
  // marchand + affichage boîte). Peuplés uniquement pour type virtual_mailbox.
  vmBoxNumber?: string;
  vmMerchant?: string;
  // #1122 — dates de RÉFÉRENCE du couloir VM, pour l'ancienneté affichée par la
  // vue « Travail VM du jour ». `createdAt` ne les remplace pas : un colis VM
  // arrive à l'entrepôt (`incomingPackage.receivedAt`) puis est payé
  // (`shippingRequest.paidAt`), et c'est depuis CES instants que l'attente
  // court, pas depuis la création du document.
  vmReceivedAt?: Date;
  vmPaidAt?: Date;
  // #1344 — la COMMANDE client VM sur la ligne de liste. Sans elle, l'écran ne
  // sait juger l'attente de paiement que sur `status.current`, un miroir que
  // toute écriture de statut écrase (cf. `core/models/vm-status.constants.ts`),
  // et l'agent n'a rien pour rapprocher le colis de la commande côté client :
  // ni montant, ni date de demande. Clés OMISES quand la commande n'existe pas.
  vmRequestStatus?: string;
  vmRequestTotal?: number;
  vmRequestCurrency?: string;
  vmRequestedAt?: Date;
  // #1395 — quand le devis a EXPIRÉ (re-pesée → session Stripe expirée). C'est
  // l'instant depuis lequel court l'attente d'une relance : le colis est en
  // stock, les frais courent (#1360), et le client ne peut plus payer.
  // Clé OMISE quand la commande n'a jamais expiré.
  vmRequestExpiredAt?: Date;
  // 🔴 #1837 (épic #1835) — instant d'ÉMISSION de la facture VM
  // (`virtualMailboxData.storage.invoiceIssuedAt`). C'est la seule chose qui
  // distingue un colis « reçu, à traiter » d'un colis « en stock » : il n'y a
  // pas de statut ni de phase pour le dire, les deux vivent en `vm_reception`.
  // Clé OMISE tant que la facture n'a pas été émise — et c'est cette absence
  // qui met le colis dans la file de traitement.
  vmInvoiceIssuedAt?: Date;
  // #2158 — instant où l'e-mail de facture (avec le lien de paiement) est
  // PARTI (`virtualMailboxData.storage.invoiceNotifiedAt`). Le serveur ne le
  // pose qu'après un envoi réussi (`vm-invoice-ready.ts`) : un colis facturé
  // SANS cette clé est un client qui n'a pas reçu son lien. Clé OMISE sinon.
  vmInvoiceNotifiedAt?: Date;
  // #1354 — les deux dates qui font la lecture de la vue « Livraisons » :
  // quand le colis est arrivé (scan RDC) → quand il a été retiré/livré.
  // Aucun champ unique ne les porte : selon le chemin qui a écrit le document
  // (CF `transitionShipmentStatus`, écran legacy, app livreur), la date est
  // dans `congoArrival.*`, `timestamps.*`, `delivery.*` ou seulement dans le
  // journal `statusHistory`. Cf. `extractArrivalScanAt` / `extractDeliveredAt`.
  // Clés OMISES quand la date n'existe pas (jamais posées à `undefined`).
  arrivalScanAt?: Date;
  deliveredAt?: Date;
}

/**
 * #1153 — un ACHAT DÉCLARÉ par un client VM, pas encore reçu à l'entrepôt.
 *
 * ⚠️ Ce n'est **pas un colis**. Tant qu'un agent n'a pas scanné, aucun document
 * `shipments` n'existe : le colis est physiquement là ou en route, mais
 * informatiquement inexistant — aucune liste ne peut le montrer. Le seul signal
 * de ce qui va arriver est **déclaratif** : le client annonce son achat
 * (`vmPendingPurchases`, statut `awaiting_arrival` / `partially_received`).
 *
 * D'où le vocabulaire imposé côté écran : ce sont des **attendus**, jamais
 * « des colis à enregistrer ». Un attendu peut ne jamais arriver, arriver en
 * plusieurs fois, ou correspondre à un colis déjà reçu sans avoir été rapproché.
 *
 * Ce que ça sert : à l'arrivée d'un colis dont l'étiquette est illisible,
 * savoir que trois clients attendent un Zara permet souvent de l'identifier
 * sans passer par « non identifié ».
 */
export interface VmPendingPurchaseListItem {
  id: string;
  boxNumber: string;
  merchant: string;
  orderNumber: string;
  /** Suivi annoncé par le client chez le transporteur d'origine (souvent absent). */
  externalTracking: string | null;
  description: string | null;
  estimatedValue: number | null;
  /** Types canoniques déclarés, normalisés depuis le scalaire historique ou le tableau courant. */
  orderTypes: VmOrderType[];
  /** Quand le client a DÉCLARÉ l'achat (pas quand il a commandé). */
  registeredAt: Date;
  expectedPackageCount: number;
  receivedPackageCount: number;
  /** `awaiting_arrival` | `partially_received` — les deux seuls états attendus ici. */
  status: string;
}

// Résultat recherche réception
export interface ReceptionSearchResult {
  id: string;
  trackingNumber: string;
  dpdTrackingNumber: string;
  senderName: string;
  senderEmail: string;
  senderPhone: string;
  receiverName: string;
  receiverCity: string;
  weight: number;
  currentPhase: string;
  /** `status.current` — distingue au scan un colis retourné (#410) d'un colis déjà réceptionné. */
  currentStatus?: string;
  type: string;
  createdAt: Date | null;
  isAlreadyReceived: boolean;
  /** #2164 lot 11 — réception faite : par qui, quand (« Déjà réceptionné »). */
  receivedDone?: { at: Date | null; by: string | null } | null;
  /** #2164 lot 11 — pesée Befret déjà faite : par qui, quand, combien. */
  weighedDone?: { at: Date | null; by: string | null; kg: number } | null;
  /** Statut paiement Stripe (`paymentFlow.paymentStatus`) — bandeau bloquant au scan (#403). */
  paymentStatus?: string;
  /** État du traitement post-paiement (`webhookStatus.overallStatus`) — DPD/label (#403). */
  webhookOverallStatus?: string;

  // VM-specific fields
  vmBoxNumber?: string;
  vmBoxOwnerName?: string;
  vmRegistered?: boolean;        // true if incomingPackage exists (photos + weight done)
  vmPhotosCount?: number;
  vmExternalCarrier?: string;
  vmExternalTracking?: string;
  vmMerchant?: string;
  vmAlreadyWeighed?: boolean;    // true if actualWeight recorded during VM registration
  /**
   * #1396 — `virtualMailboxData.shippingRequest.status`, l'AUTORITÉ sur l'état
   * de paiement d'une commande VM.
   *
   * `currentStatus` en portait un MIROIR (`shipping_payment_pending` /
   * `consolidation_payment_pending`, #983) posé à la création de la session
   * Stripe, que toute écriture de statut écrase et que rien ne repose. Le
   * scan de réception jugeait dessus.
   */
  vmRequestStatus?: string;

  // Heavy-specific fields
  heavyCategory?: string;        // 'heavy_custom' | 'heavy_specialist'
}

// ── Fil d'activité unifié ──

export type ActivityCategory =
  | 'order'         // Commande, paiement initial
  | 'collection'    // Collecte DPD, dépôt relais
  | 'warehouse'     // Réception, pesée, écart poids, préparation
  | 'payment'       // Paiements (initial, supplément, avoir)
  | 'expedition'    // Conteneur, transit
  | 'delivery'      // Congo, distribution, livraison
  | 'notification'  // SMS/Email envoyés
  | 'admin';        // Corrections manuelles, notes

export interface ActivityEvent {
  id: string;
  category: ActivityCategory;
  status: string;
  label: string;
  description?: string;
  timestamp: Date | string;
  source: string;
  operator?: string | { uid: string; name: string; email?: string };
  details?: Record<string, any>;
  automated: boolean;
}

export const CATEGORY_CONFIG: Record<ActivityCategory, { label: string; icon: string; color: string }> = {
  order:        { label: 'Commande',      icon: 'fa-receipt',       color: '#9E9E9E' },
  collection:   { label: 'Collecte',      icon: 'fa-truck-pickup',  color: '#2196F3' },
  warehouse:    { label: 'Entrepôt',      icon: 'fa-warehouse',     color: '#FF9800' },
  payment:      { label: 'Paiement',      icon: 'fa-credit-card',   color: '#4CAF50' },
  expedition:   { label: 'Expédition',    icon: 'fa-plane',         color: '#9C27B0' },
  delivery:     { label: 'Livraison',     icon: 'fa-truck',         color: '#E91E63' },
  notification: { label: 'Notification',  icon: 'fa-bell',          color: '#00BCD4' },
  admin:        { label: 'Admin',         icon: 'fa-user-shield',   color: '#607D8B' },
};

// Résultat pesée
export interface WeighResult {
  declaredWeight: number;
  befretWeight: number;
  delta: number;
  deltaPercentage: number;
  hasFinancialImpact: boolean;
}

// Config phases (labels, couleurs, icônes)
export const PHASE_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  preparation:        { label: 'Préparation',      color: '#9E9E9E', icon: 'fa-clock' },
  order:              { label: 'Commande',          color: '#9E9E9E', icon: 'fa-receipt' },
  dpd_collection:     { label: 'Collecte DPD',     color: '#2196F3', icon: 'fa-truck-pickup' },
  collected_europe:   { label: 'Transit Europe',    color: '#03A9F4', icon: 'fa-route' },
  warehouse:          { label: 'Entrepôt',          color: '#FF9800', icon: 'fa-warehouse' },
  befret_transit:     { label: 'Transit Congo',     color: '#9C27B0', icon: 'fa-plane' },
  congo_distribution: { label: 'Distribution',      color: '#E91E63', icon: 'fa-truck' },
  delivered:          { label: 'Livré',             color: '#4CAF50', icon: 'fa-check-circle' },
  // #1153 — « VM Réception » décrivait le GESTE d'arrivée, pas l'état : un colis
  // stocké depuis trois semaines portait un badge qui disait « on vient de le
  // recevoir ». L'état, lui, est le stock (l'attente de paiement, elle, vit dans
  // `status.current` et a son propre badge — #1121/#983).
  vm_reception:       { label: 'VM En stock',       color: '#00BCD4', icon: 'fa-inbox' },
  // #1121 — phase jamais écrite par le serveur (reliquat #424) : retirée des
  // lectures (onglets, compteurs, lots de queries). Le label reste pour
  // afficher proprement un éventuel doc legacy.
  vm_consolidation:   { label: 'VM Consolidation',  color: '#00ACC1', icon: 'fa-boxes-stacked' },
  vm_unidentified:    { label: 'VM Non identifié',  color: '#B45309', icon: 'fa-circle-question' },
  heavy_processing:   { label: 'Colis lourd',        color: '#795548', icon: 'fa-weight-hanging' },
  heavy_collection:   { label: 'Collecte lourd',     color: '#8D6E63', icon: 'fa-truck-loading' },
  heavy_delivery:     { label: 'Livraison lourd',    color: '#6D4C41', icon: 'fa-dolly' },
  return_to_sender:   { label: 'Retour expéditeur', color: '#F44336', icon: 'fa-undo' },
  incident:           { label: 'Incident',           color: '#D32F2F', icon: 'fa-triangle-exclamation' },
  // Alternate phase names from shipment-status-initializer (backend inconsistency)
  dpd_phase:          { label: 'Collecte DPD',     color: '#2196F3', icon: 'fa-truck-pickup' },
  befret_phase:       { label: 'Transit Befret',    color: '#9C27B0', icon: 'fa-plane' },
};

/**
 * Libellé de la pesée Befret d'un colis Virtual Mailbox (#1348).
 *
 * Sur un colis VM, `parcelInfo.weight` n'est ni un poids DÉCLARÉ par le client
 * ni un poids annoncé par le transporteur : `processVmPackage`,
 * `recordVmMeasurement` et la convergence au paiement y recopient la pesée de
 * notre balance. Un seul libellé, partagé par le poste de pesée, la carte de
 * scan de la réception et la fiche de préparation (#1825), pour que l'agent
 * lise la même mesure sous le même nom partout.
 *
 * #1837 — « pesée réception VM » est devenu faux : depuis l'épic #1835, la
 * réception VM ne pèse plus. La pesée a lieu au TRAITEMENT du colis.
 */
export const VM_BEFRET_WEIGHT_LABEL = 'Poids Befret (pesée au traitement VM)';

// Phases post-warehouse (pour déterminer isAlreadyReceived).
// #403 : complété avec retour expéditeur, consolidation VM et livraison lourd —
// un colis dans ces phases est DÉJÀ passé par l'entrepôt et ne doit pas être
// re-scannable (écrasement en `warehouse`). `vm_reception` reste hors liste :
// géré par les guards dédiés du composant réception (#1120) —
// isVmAwaitingPayment (demande d'expédition/consolidation impayée, statuts
// shipping_payment_pending / consolidation_payment_pending, #983) et
// isVmInStock (colis en stock sans demande client). Dans les deux cas la
// réception physique VM est déjà faite à l'enregistrement et la convergence
// vers le flux standard passe par le paiement (webhook Stripe, #1046), jamais
// par un scan entrepôt. Les phases heavy pré-entrepôt
// (heavy_processing/heavy_collection) restent réceptionnables.
// #1121 — `vm_consolidation` retirée : phase jamais écrite par le serveur
// (reliquat #424), l'entrée était morte.
export const POST_WAREHOUSE_PHASES = [
  'warehouse', 'befret_transit', 'befret_phase', 'congo_distribution', 'delivered',
  'return_to_sender', 'heavy_delivery'
];

// ── Conteneur d'expédition ──

export interface ContainerContact {
  name: string;
  role: string;
  phone: string;
  company?: string;
  location: string;
  order: number;
}

export type ShippingMethodSubType = 'cargo_lta' | 'mco' | 'tag';

export interface ContainerFlight {
  from: FlightCity;
  to: FlightCity;
  departureAt: Date;
  arrivalAt: Date;
  source: 'schedule' | 'manual';
}

export interface ExpeditionContactSlot {
  memberId: string;
  memberName: string;
  confirmedAt?: Date;
}

export interface ShippingContainer {
  id: string;
  containerNumber: string;
  transportMode: 'air' | 'maritime';
  maxWeightKg: number;
  maxWeightType?: 'air_23' | 'maritime_32' | 'custom';
  currentWeightKg: number;
  status: 'open' | 'closed' | 'dispatched' | 'in_transit' | 'arrived' | 'unpacked';
  shipmentIds: string[];
  shipmentCount: number;
  destinationCity: string;
  departure: {
    closedAt?: Date;
    closedBy?: string;
    departureWeight: number;
    sealNumber?: string;
    dispatchedAt?: Date;
    estimatedArrival?: Date;
  };
  contactChain: ContainerContact[];

  // Shipping method details
  shippingMethodDetails?: {
    method: 'air' | 'maritime';
    subType?: ShippingMethodSubType;
    partnerId?: string;
    partnerName?: string;
  };

  /** #2256 — le vol de ce groupage : issu des horaires du partenaire, ou saisi à la main. */
  flight?: ContainerFlight;

  // Filmage (wrapping) confirmation
  filmage?: {
    confirmed: boolean;
    confirmedAt?: Date;
    confirmedBy?: string;
  };

  // Container label PDF URL
  containerLabelUrl?: string;

  // Photo of wrapped groupage
  groupagePhotoUrl?: string;

  // Structured contact chain for expedition
  expeditionContacts?: {
    responsableGroupageA?: ExpeditionContactSlot;
    contactExpedition?: ExpeditionContactSlot;
    contactReception?: ExpeditionContactSlot;
    responsableGroupageB?: ExpeditionContactSlot;
    openedBy?: string;
    openedAt?: Date;
    closedBy?: string;
    closedAt?: Date;
  };

  // Expedition step tracking
  expeditionStep?: 'groupage' | 'shipping_method' | 'contact' | 'ready_to_dispatch' | 'dispatched';

  arrival?: {
    arrivedAt?: Date;
    arrivalWeight?: number;
    weightDelta?: number;
    condition: 'good' | 'damaged' | 'partial';
    receivedBy?: string;
    groupagePhotoUrl?: string;
    weighedAt?: Date;
    weighingAlert?: boolean;
  };
  unpacking?: {
    startedAt?: Date;
    completedAt?: Date;
    scannedShipmentIds: string[];
    missingShipmentIds: string[];
  };
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  notes?: string;
}

export const CONTAINER_STATUS_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  open:       { label: 'Ouvert',     color: '#FF9800', icon: 'fa-box-open' },
  closed:     { label: 'Fermé',      color: '#2196F3', icon: 'fa-box' },
  dispatched: { label: 'Expédié',    color: '#9C27B0', icon: 'fa-paper-plane' },
  in_transit: { label: 'En transit', color: '#9C27B0', icon: 'fa-route' },
  arrived:    { label: 'Arrivé',     color: '#E91E63', icon: 'fa-map-marker-alt' },
  unpacked:   { label: 'Dégroupé',   color: '#4CAF50', icon: 'fa-check-double' },
};

// ── Partenaire de livraison ──

export interface DeliveryPartner {
  id: string;
  name: string;
  type: 'individual' | 'company';
  zones: string[];
  contact: { phone: string; email?: string; address?: string };
  vehicleType: 'motorcycle' | 'car' | 'van' | 'truck';
  status: 'active' | 'inactive';
  stats: { totalDeliveries: number; successRate: number; lastDeliveryDate?: Date };
  // Internal Befret fleet vs external partner
  isInternal?: boolean;
  location?: string;  // For internal: 'Kinshasa' | 'Lubumbashi'
  createdAt: Date;
  updatedAt: Date;
}

export const VEHICLE_TYPE_LABELS: Record<string, string> = {
  motorcycle: 'Moto',
  car: 'Voiture',
  van: 'Camionnette',
  truck: 'Camion',
};

export const DELIVERY_ZONES = [
  'Kinshasa-Gombe', 'Kinshasa-Limete', 'Kinshasa-Ngaliema', 'Kinshasa-Barumbu',
  'Kinshasa-Kalamu', 'Kinshasa-Kintambo', 'Kinshasa-Lingwala', 'Kinshasa-Bandalungwa',
  'Kinshasa-Matete', 'Kinshasa-Lemba', 'Kinshasa-Ngiri-Ngiri', 'Kinshasa-Masina',
  'Lubumbashi-Centre', 'Lubumbashi-Kampemba', 'Lubumbashi-Katuba', 'Lubumbashi-Ruashi',
];

// ── Membre d'équipe ──

export type ContractType = 'cdi' | 'cdd' | 'stagiaire' | 'freelance';
export type InvitationStatus = 'none' | 'pending' | 'accepted' | 'expired';

export interface ContractRenewal {
  previousEndDate: string;               // ISO date — the end date before renewal
  newEndDate: string;                    // ISO date — the new end date
  renewedAt: Date;                       // when the renewal was recorded
  renewedBy: string;                     // uid of the RH/admin who recorded it
  reason?: string;
}

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  location: string;
  phone: string;
  email: string;
  photoUrl?: string;
  status: 'active' | 'inactive' | 'pending' | 'suspended';
  // RH
  contractType?: ContractType;
  department?: string;
  invitationStatus?: InvitationStatus;
  invitationToken?: string;
  invitationExpiresAt?: Date;
  invitedBy?: string;
  userUid?: string;
  activatedAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  // HR extended fields
  hireDate?: Date;
  contractStartDate?: Date;
  contractEndDate?: Date;
  probationEndDate?: Date;
  contractRenewals?: ContractRenewal[];

  // Personal info (self-service, editable in "Mon profil")
  personalPhone?: string;
  personalAddress?: string;
  emergencyContact?: { name: string; phone: string; relationship?: string };
  iban?: string;
  nationality?: string;
  dateOfBirth?: Date;

  onboardingStatus?: string;
  onboardingSteps?: {
    invitationSent?: Date;
    accountActivated?: Date;
    equipmentAssigned?: Date; // #797 — auto au assignAsset, ou manuel
    documentsUploaded?: Date;
    trainingAssigned?: Date;
    trainingCompleted?: Date;
    onboardingComplete?: Date;
  };
  managerId?: string;
  onboardingTicketId?: string;  // Ticket parent onboarding (service_request)
  recruitmentId?: string;       // Lien vers le processus de recrutement
  // RBAC
  directRoleIds?: string[];  // rôles assignés directement à l'employé
  groupIds?: string[];       // groupes dont l'employé est membre
  // Source tracking
  source?: 'backoffice' | 'invite';
}

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  cdi: 'CDI',
  cdd: 'CDD',
  stagiaire: 'Stagiaire',
  freelance: 'Freelance',
};

export const CONTRACT_TYPE_COLORS: Record<ContractType, string> = {
  cdi: '#2196F3',
  cdd: '#FF9800',
  stagiaire: '#9C27B0',
  freelance: '#607D8B',
};

export const DEPARTMENT_LABELS: Record<string, string> = {
  direction: 'Direction',
  logistique: 'Logistique',
  support: 'Support',
  finance: 'Finance',
  it: 'IT',
  marketing: 'Marketing',
};

export const INVITATION_STATUS_CONFIG: Record<InvitationStatus, { label: string; color: string; icon: string }> = {
  none: { label: 'Direct', color: '#9E9E9E', icon: 'fa-minus' },
  pending: { label: 'Invitation envoyée', color: '#FF9800', icon: 'fa-clock' },
  accepted: { label: 'Activé', color: '#4CAF50', icon: 'fa-check-circle' },
  expired: { label: 'Expirée', color: '#F44336', icon: 'fa-times-circle' },
};

export const TEAM_ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  rh: 'RH',
  warehouse: 'Entrepôt',
  transitory: 'Transitaire',
  manager: 'Manager',
  topManager: 'Top Manager',
  driver: 'Chauffeur',
  customs: 'Douane',
};

export const TEAM_ROLE_COLORS: Record<string, string> = {
  super_admin: '#D32F2F',
  admin: '#C62828',
  rh: '#7B1FA2',
  warehouse: '#FF9800',
  transitory: '#9C27B0',
  manager: '#2196F3',
  topManager: '#1565C0',
  driver: '#4CAF50',
  customs: '#E91E63',
};

// ── Tri par destination + Classement ──

export interface ParcelNatureGroup {
  key: string;
  destinationZone: string;
  transportMode: 'air' | 'maritime';
  parcelType: string;
  shipments: ShipmentListItem[];
  totalWeight: number;
  count: number;
}

export const DESTINATION_ZONE_CONFIG: Record<string, { label: string; color: string; shortLabel: string }> = {
  'Kinshasa-Gombe':      { label: 'Kinshasa - Gombe',      color: '#2196F3', shortLabel: 'GOMBE' },
  'Kinshasa-Limete':     { label: 'Kinshasa - Limete',     color: '#4CAF50', shortLabel: 'LIMETE' },
  'Kinshasa-Ngaliema':   { label: 'Kinshasa - Ngaliema',   color: '#FF9800', shortLabel: 'NGALIEMA' },
  'Kinshasa-Centre':     { label: 'Kinshasa - Centre',     color: '#9C27B0', shortLabel: 'CENTRE' },
  'Lubumbashi-Centre':   { label: 'Lubumbashi - Centre',   color: '#E91E63', shortLabel: 'LUB-CENTRE' },
  'Lubumbashi-Kampemba': { label: 'Lubumbashi - Kampemba', color: '#00BCD4', shortLabel: 'KAMPEMBA' },
};

export const TRANSPORT_MODE_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  'air':      { label: 'Aerien',   color: '#2196F3', icon: 'fa-plane' },
  'maritime': { label: 'Maritime', color: '#9C27B0', icon: 'fa-ship' },
};

export const PARCEL_TYPE_LABELS: Record<string, string> = {
  'standard':     'Standard',
  'heavy':        'Colis lourd',
  'vm':           'Virtual Mailbox',
  'consolidated': 'Consolide',
};

// ── Condition (nature) colis pour classement ──

export type ParcelCondition = 'ok' | 'empty' | 'dangerous' | 'damaged' | 'payment_pending';

export const PARCEL_CONDITION_CONFIG: Record<ParcelCondition, { label: string; color: string; icon: string }> = {
  ok:              { label: 'Safe',              color: '#4CAF50', icon: 'fa-check-circle' },
  dangerous:       { label: 'Dangereux',         color: '#B71C1C', icon: 'fa-exclamation-triangle' },
  empty:           { label: 'Vide',              color: '#F44336', icon: 'fa-box-open' },
  damaged:         { label: 'Endommage',         color: '#FF9800', icon: 'fa-hammer' },
  payment_pending: { label: 'Attente paiement',  color: '#FF6F00', icon: 'fa-clock' },
};

// ── Partenaire d'expédition ──

export type ShippingPartnerStatus = 'active' | 'inactive' | 'archived';

export interface ShippingPartner {
  id: string;
  name: string;
  shippingMethod: 'air' | 'maritime';
  subType?: ShippingMethodSubType;
  contact: {
    phone?: string;
    email?: string;
    address?: string;
  };
  status: ShippingPartnerStatus;
  /** #2256 — vols réguliers, par liaison. Aérien seulement. */
  flightSchedules?: FlightSchedule[];
  // V2 versioning — quand un partenaire est archivé car remplacé
  replacedById?: string;
  archivedReason?: string;
  createdAt: Date;
  updatedAt: Date;
}

export const SHIPPING_METHOD_SUBTYPE_CONFIG: Record<ShippingMethodSubType, { label: string; icon: string }> = {
  cargo_lta: { label: 'Cargo-LTA', icon: 'fa-boxes-packing' },
  mco:       { label: 'MCO',       icon: 'fa-plane' },
  tag:       { label: 'Tag',       icon: 'fa-tag' },
};

// ── Team logistique (expedition) ──

export type LogisticsRole = 'responsable_groupage' | 'contact_expedition' | 'contact_reception';

export interface LogisticsTeamMember {
  id: string;
  name: string;
  pseudonyme?: string;
  phoneOperator: string;
  phoneWhatsApp?: string;
  role: LogisticsRole;
  photoUrl?: string;
  isInternal: boolean;
  status: 'active' | 'inactive';
  createdAt: Date;
  updatedAt: Date;
}

export const LOGISTICS_ROLE_CONFIG: Record<LogisticsRole, { label: string; color: string; icon: string; description: string }> = {
  responsable_groupage: { label: 'Responsable Groupage (CB)', color: '#2196F3', icon: 'fa-user-tie',       description: 'Staff Befret qui remet/recoit le groupage' },
  contact_expedition:   { label: 'Contact Expedition',        color: '#9C27B0', icon: 'fa-plane-departure', description: 'Personne qui gere l\'envoi (voyageur ou agent aeroport)' },
  contact_reception:    { label: 'Contact Reception',         color: '#E91E63', icon: 'fa-plane-arrival',   description: 'Personne qui recoit au point intermediaire' },
};

// ── Expedition constants ──

export const WEIGHT_PRESETS: Record<string, { label: string; maxKg: number }> = {
  air_23:      { label: 'Aerien (23 kg)',   maxKg: 23 },
  maritime_32: { label: 'Maritime (32 kg)', maxKg: 32 },
  custom:      { label: 'Personnalise',     maxKg: 0 },
};

export const EXPEDITION_STEP_CONFIG: Record<string, { label: string; icon: string; order: number }> = {
  groupage:          { label: 'Groupage',        icon: 'fa-cubes',           order: 1 },
  shipping_method:   { label: 'Mode d\'envoi',   icon: 'fa-plane-departure', order: 2 },
  contact:           { label: 'Contact',         icon: 'fa-address-book',    order: 3 },
  ready_to_dispatch: { label: 'Pret a expedier', icon: 'fa-check-double',    order: 4 },
  dispatched:        { label: 'Expedie',         icon: 'fa-paper-plane',     order: 5 },
};

// ── Dégroupage parcel status ──

export type ParcelDegroupageStatus = 'validated' | 'blocked' | 'pending';

export const DEGROUPAGE_STATUS_CONFIG: Record<ParcelDegroupageStatus, { label: string; color: string; icon: string }> = {
  validated: { label: 'Validé',     color: '#4CAF50', icon: 'fa-check-circle' },
  blocked:   { label: 'Bloqué',     color: '#F44336', icon: 'fa-ban' },
  pending:   { label: 'En attente', color: '#FF9800', icon: 'fa-hourglass-half' },
};

// ══════════════════════════════════════════
// SHARED CONFIGS — Reusable across components
// ══════════════════════════════════════════

/** Shared tab config interface — used by shipment-list, container-list, ticket-list, sorting, delivery-list */
export interface TabConfig {
  id: string;
  label: string;
  /** #2220 — clé i18n, prioritaire sur `label` (les libellés retouchés passent en clés). */
  labelKey?: string;
  color: string;
  icon?: string;
  phases?: string[];
  statuses?: string[];
  count?: number;
}

/** Global color palette — single source of truth for all UI colors */
export const COLOR_PALETTE = {
  blue:       '#2196F3',
  dark_blue:  '#1565C0',
  light_blue: '#03A9F4',
  orange:     '#FF9800',
  purple:     '#9C27B0',
  pink:       '#E91E63',
  green:      '#4CAF50',
  dark_green: '#1B5E20',
  light_green:'#8BC34A',
  red:        '#F44336',
  dark_red:   '#B71C1C',
  cyan:       '#00BCD4',
  grey:       '#607D8B',
  brown:      '#795548',
  amber:      '#FFC107',
  teal:       '#009688',
  indigo:     '#3F51B5',
  deep_orange:'#FF5722',
} as const;

/** Reusable icon palette for forms (departments, categories, etc.) */
export const ICON_OPTIONS: string[] = [
  'fa-briefcase', 'fa-warehouse', 'fa-headset', 'fa-calculator',
  'fa-cogs', 'fa-truck', 'fa-laptop-code', 'fa-chart-line',
  'fa-users', 'fa-shield-alt', 'fa-bullhorn', 'fa-tools',
  'fa-file-contract', 'fa-globe', 'fa-handshake', 'fa-clipboard-list',
  'fa-box', 'fa-plane', 'fa-ship', 'fa-credit-card',
];

/** Reusable color options for forms (departments, categories, etc.) */
export const COLOR_OPTIONS: string[] = [
  '#1565C0', '#FF9800', '#9C27B0', '#4CAF50',
  '#F44336', '#00BCD4', '#795548', '#607D8B',
  '#E91E63', '#3F51B5', '#009688', '#FF5722',
];

// ── Shipment list tab presets (derived from PHASE_CONFIG) ──

/**
 * #1153 — LES ONGLETS D'ÉTAPE DÉCRIVENT LE PIPELINE COMMUN, PAS LES COULOIRS.
 *
 * Befret traite **trois types de colis** dont une partie du traitement diffère :
 * le **standard**, le **lourd** et la **VM**. Lourd et VM **convergent** avec le
 * standard une fois deux conditions réunies : le colis est **chez nous** ET le
 * client a **payé** (statut `received_at_warehouse`, phase `warehouse` — pour la
 * VM c'est `handle-vm-payment-webhook`, pour le lourd la fin de la phase
 * `heavy_collection`). Avant ce point, ce que l'agent fait est **différent**.
 *
 * D'où la structure : les onglets `collection → warehouse → transit →
 * distribution → delivered` décrivent le pipeline **post-convergence**, commun
 * aux trois types. Les phases **pré-convergence** du lourd et de la VM ont
 * chacune **leur** onglet, parce que le geste de l'agent y est autre (établir un
 * devis et planifier une collecte spécialisée ; garder un colis en stock en
 * attendant une décision client qui peut ne jamais venir).
 *
 * Ce qui était faux avant (#1153) :
 *   • le **lourd était éparpillé sur 3 onglets** (`heavy_collection` en
 *     « En collecte », `heavy_processing` en « Entrepôt » — alors que dans les
 *     DEUX cas le colis est encore **chez le client**) ;
 *   • la **VM était dans « En collecte »**, un onglet qui dit « pas encore
 *     arrivé », alors que le colis est à Tubize, scanné et photographié,
 *     parfois depuis des semaines.
 *
 * Ce qui NE bouge pas : `heavy_delivery` est la **livraison finale** en RDC
 * (progression 85-95 %), donc **post-convergence** — elle reste en
 * « Distribution », avec `congo_distribution`. Idem `return_to_sender`, qui suit
 * un passage entrepôt.
 *
 * ⚠️ L'union des `phases` de ces onglets doit rester égale à `ALL_PHASES` :
 * une phase absente de tous les onglets est un colis **invisible**. Verrouillé
 * par `shipment-list-tabs.spec.ts`.
 */
export const SHIPMENT_LIST_TABS: TabConfig[] = [
  { id: 'all',          label: 'Tous',           phases: [],                                                                        color: COLOR_PALETTE.grey },
  // Pré-entrepôt STANDARD uniquement : « en collecte » veut dire « le colis
  // n'est pas encore chez nous », et c'est vrai de ces cinq phases.
  { id: 'collection',   label: 'En collecte',    phases: ['preparation', 'order', 'dpd_collection', 'dpd_phase', 'collected_europe'], color: COLOR_PALETTE.blue },
  // Couloir LOURD pré-convergence : demande → analyse → devis → paiement
  // (`heavy_processing`), puis collecte spécialisée sur site → transit vers
  // l'entrepôt (`heavy_collection`). Le colis est chez le client tout du long.
  { id: 'heavy_intake', label: 'Lourds — devis & collecte', phases: ['heavy_processing', 'heavy_collection'],                        color: COLOR_PALETTE.brown },
  // Couloir VM pré-convergence : le colis est PHYSIQUEMENT chez nous (scanné,
  // pesé, photographié) mais n'appartient pas encore au flux d'expédition — il
  // attend que le client paie son expédition (#1851 : le geste « demander
  // l'expédition » a été supprimé par #1345).
  // `vm_unidentified` = même stock, boîte inconnue (#1045).
  // #2220 — « en stock » ne dit ni ce qui est fait ni ce qui reste : l'onglet
  // regroupe les colis VM PHYSIQUEMENT à l'entrepôt, avant leur paiement.
  { id: 'vm_stock',     label: 'VM — à l\'entrepôt', labelKey: 'VM.BACKOFFICE.LIST.TAB_VM_WAREHOUSE', phases: ['vm_reception', 'vm_unidentified'],                                        color: COLOR_PALETTE.cyan },
  { id: 'warehouse',    label: 'Entrepôt',       phases: ['warehouse', 'return_to_sender'],                                          color: COLOR_PALETTE.orange },
  { id: 'transit',      label: 'Transit',        phases: ['befret_transit', 'befret_phase'],                                         color: COLOR_PALETTE.purple },
  { id: 'distribution', label: 'Distribution',   phases: ['congo_distribution', 'heavy_delivery'],                                   color: COLOR_PALETTE.pink },
  { id: 'delivered',    label: 'Livrés',         phases: ['delivered'],                                                              color: COLOR_PALETTE.green },
  // #1401 — parcours interrompu : perdu, endommagé, mal emballé, marchandise
  // dangereuse, saisi en douane, commande assistée expirée/annulée. Sans cet
  // onglet la phase `incident` n'appartenait à aucune liste — le colis
  // disparaissait de l'écran au lieu d'appeler une décision.
  { id: 'incident',     label: 'Incidents',      phases: ['incident'],                                                               color: COLOR_PALETTE.red },
];

// ── Container list tab presets (derived from CONTAINER_STATUS_CONFIG) ──

export const CONTAINER_LIST_TABS: TabConfig[] = [
  { id: 'all',        label: 'Tous',       color: COLOR_PALETTE.grey },
  { id: 'open',       label: 'Ouverts',    color: COLOR_PALETTE.orange },
  { id: 'closed',     label: 'Fermés',     color: COLOR_PALETTE.blue },
  { id: 'dispatched', label: 'Expédiés',   color: COLOR_PALETTE.purple },
  { id: 'arrived',    label: 'Arrivés',    color: COLOR_PALETTE.pink },
  { id: 'unpacked',   label: 'Dégroupés',  color: COLOR_PALETTE.green },
];

// ── Delivery list tab presets ──

export const DELIVERY_LIST_TABS: TabConfig[] = [
  { id: 'pending',   label: 'En attente', color: COLOR_PALETTE.orange },
  { id: 'failed',    label: 'Échouées',   color: COLOR_PALETTE.red },
  { id: 'delivered', label: 'Livrées',    color: COLOR_PALETTE.green },
  { id: 'all',       label: 'Toutes',     color: COLOR_PALETTE.grey },
];

// ── Sorting tab presets ──

export const SORTING_TABS: TabConfig[] = [
  { id: 'all',            label: 'Tous',             color: COLOR_PALETTE.grey },
  { id: 'home_delivery',  label: 'Livraison à domicile en RDC', color: COLOR_PALETTE.dark_blue },
  { id: 'warehouse',      label: 'Point de retrait', color: COLOR_PALETTE.purple },
];

// ── Delivery zone config (sorting/distribution) ──

export const DELIVERY_ZONE_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  home_delivery: { label: 'Zone livraison à domicile en RDC',   icon: 'fa-home',      color: '#1565C0' },
  warehouse:     { label: 'Zone retrait en agence Befret',       icon: 'fa-warehouse', color: '#7B1FA2' },
};

// ── Condition verification config (preparation-detail) ──

export type VerificationCondition = 'ok' | 'empty' | 'dangerous' | 'payment_pending' | 'damaged';

export interface ConditionOption {
  value: VerificationCondition;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  blocking: boolean;
  notificationType?: string;
  description: string;
}

export const CONDITION_OPTIONS: ConditionOption[] = [
  {
    value: 'ok',
    label: 'OK',
    icon: 'fa-check-circle',
    color: '#2E7D32',
    bgColor: '#E8F5E9',
    blocking: false,
    description: 'Colis conforme, pret pour l\'etiquetage',
  },
  {
    value: 'empty',
    label: 'Colis vide',
    icon: 'fa-box-open',
    color: '#C62828',
    bgColor: '#FFEBEE',
    blocking: true,
    notificationType: 'empty_parcel',
    description: 'Le colis est vide ou ne contient rien',
  },
  {
    value: 'dangerous',
    label: 'Dangereux / Suspect',
    icon: 'fa-radiation',
    color: '#B71C1C',
    bgColor: '#FFEBEE',
    blocking: true,
    notificationType: 'dangerous_goods',
    description: 'Contenu dangereux, interdit ou suspect',
  },
  {
    value: 'payment_pending',
    label: 'Attente paiement',
    icon: 'fa-clock',
    color: '#E65100',
    bgColor: '#FFF3E0',
    blocking: true,
    notificationType: 'payment_pending_surcharge',
    description: 'Supplement de poids non paye',
  },
  {
    value: 'damaged',
    label: 'Endommage',
    icon: 'fa-exclamation-triangle',
    color: '#E65100',
    bgColor: '#FFF3E0',
    blocking: true,
    notificationType: 'damaged',
    description: 'Colis abime ou mal emballe',
  },
];

// ── Container arrival condition ──

export type ArrivalCondition = 'good' | 'damaged' | 'partial';

export const ARRIVAL_CONDITION_CONFIG: Record<ArrivalCondition, { label: string; color: string; icon: string }> = {
  good:    { label: 'Bon état',         color: '#4CAF50', icon: 'fa-check-circle' },
  damaged: { label: 'Endommagé',        color: '#F44336', icon: 'fa-exclamation-triangle' },
  partial: { label: 'Contenu partiel',  color: '#FF9800', icon: 'fa-minus-circle' },
};
